import * as THREE from 'three';

// ---------------------------------------------------------------------------
// One depth map for the whole sea.
//
// The water shader needs to know how deep it is anywhere, to colour the
// shallows and draw the foam line. Sampling every island's own heightmap would
// mean a texture lookup per island per pixel; instead each island stamps its
// heights into a single coarse map covering the whole sea room.
//
// Coarse is fine: the water only needs the shape of the ground, not its
// voxels. Where several island cells fall in one texel the highest wins, so a
// spit of land is never averaged away into a shoal.
// ---------------------------------------------------------------------------

export function buildWorldDepth(islands, { extent, resolution = 512 }) {
    const data = new Uint8Array(resolution * resolution);
    const origin = -extent / 2;
    const perTexel = extent / resolution;

    for (const { polder } of islands) {
        const { size, heights } = polder;
        for (let gz = 0; gz < size; gz++) {
            for (let gx = 0; gx < size; gx++) {
                const h = heights[gz * size + gx];
                if (h <= 0) continue;
                const at = polder.toWorld(gx, gz);
                const tx = Math.floor((at.x - origin) / perTexel);
                const tz = Math.floor((at.z - origin) / perTexel);
                if (tx < 0 || tz < 0 || tx >= resolution || tz >= resolution) continue;
                const i = tz * resolution + tx;
                if (h > data[i]) data[i] = Math.min(255, h);
            }
        }
    }

    const texture = new THREE.DataTexture(data, resolution, resolution, THREE.RedFormat);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.needsUpdate = true;

    return { texture, origin, extent, resolution };
}
